import { makeStyles, mergeClasses, SpinButton } from '@fluentui/react-components';
import { Add, Close } from '@material-symbols-svg/react/rounded/w500';

import { formatHM, HOURS_QUANTUM, parseHM } from '../common';
import { palette, tokens, useAppButtonStyles } from '../theme';

const PRESETS = [0.25, 0.5, 1, 2, 4, 8] as const;

const formatPreset = (hours: number): string => {
    if (hours < 1) {
        return `${Math.round(hours * 60)}m`;
    }
    return `${hours}h`;
};

export type HoursPanelTarget = {
    id: number;
    title: string;
    accent: string;
    typeLabel: string;
};

export type HoursPanelProps = {
    item: HoursPanelTarget;
    currentHours: number;
    remaining: number;
    canAdd: boolean;
    onHoursChange: (value: string) => void;
    onAdd: () => void;
    onClose?: () => void;
};

export const HoursPanel: React.FC<HoursPanelProps> = ({ item, currentHours, remaining, canAdd, onHoursChange, onAdd, onClose }) => {
    const classes = useStyles();
    const buttons = useAppButtonStyles();
    const max = Math.max(HOURS_QUANTUM, remaining);

    return (
        <section className={classes.root} aria-label="Add hours to selected work item">
            <header className={classes.target}>
                <span className={classes.typeDot} style={{ backgroundColor: item.accent }} aria-label={item.typeLabel} />
                <span className={classes.idText}>#{item.id}</span>
                <span className={classes.titleText}>{item.title}</span>
                {onClose && (
                    <button type="button" className={classes.closeBtn} aria-label="Cancel add entry" onClick={onClose}>
                        <Close className={classes.closeIcon} aria-hidden />
                    </button>
                )}
            </header>

            <div className={classes.presets} role="group" aria-label="Pick a duration">
                {PRESETS.map((preset) => {
                    const active = Math.abs(currentHours - preset) < HOURS_QUANTUM / 2;
                    const enabled = preset <= max;
                    return (
                        <button
                            key={preset}
                            type="button"
                            aria-pressed={active}
                            className={mergeClasses(classes.preset, active && classes.presetActive)}
                            disabled={!enabled}
                            onClick={() => onHoursChange(String(preset))}
                        >
                            {formatPreset(preset)}
                        </button>
                    );
                })}
            </div>

            <div className={classes.commitRow}>
                <div className={classes.customField}>
                    <span className={classes.customLabel}>Custom time</span>
                    <SpinButton
                        className={classes.spin}
                        value={currentHours}
                        displayValue={formatHM(currentHours)}
                        min={HOURS_QUANTUM}
                        max={max}
                        step={HOURS_QUANTUM}
                        onChange={(_, data) => {
                            if (data.value !== undefined && data.value !== null) {
                                onHoursChange(String(data.value));
                                return;
                            }
                            if (typeof data.displayValue === 'string') {
                                const parsed = parseHM(data.displayValue);
                                if (parsed !== null) {
                                    onHoursChange(String(parsed));
                                }
                            }
                        }}
                    />
                </div>

                <button
                    type="button"
                    className={mergeClasses(buttons.primary, classes.addButton)}
                    disabled={!canAdd}
                    onClick={onAdd}
                    aria-label={`Add ${formatHM(currentHours)} entry`}
                >
                    <Add className={classes.addIcon} aria-hidden />
                    <span>Add {formatHM(currentHours)}</span>
                </button>
            </div>
        </section>
    );
};

const useStyles = makeStyles({
    root: {
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: '14px',
        boxShadow: palette.softShadowSm,
    },

    target: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
    },
    closeBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        marginLeft: 'auto',
        border: 'none',
        backgroundColor: 'transparent',
        color: palette.dimText,
        borderRadius: '50%',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.panelMuted,
            color: tokens.colorNeutralForeground1,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '2px',
        },
    },
    closeIcon: {
        width: '18px',
        height: '18px',
    },
    typeDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    idText: {
        fontSize: '12.5px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.dimText,
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
    },
    titleText: {
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },

    presets: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: '8px',
    },
    preset: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '40px',
        padding: '0 8px',
        backgroundColor: palette.panelBg,
        color: tokens.colorNeutralForeground1,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: palette.pillRadius,
        fontFamily: 'inherit',
        fontSize: '14px',
        fontWeight: tokens.fontWeightBold,
        fontVariantNumeric: 'tabular-nums',
        cursor: 'pointer',
        transition: 'background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover:not(:disabled)': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
            border: `1px solid ${palette.accentBg}`,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '2px',
        },
        '&:disabled': {
            opacity: 0.35,
            cursor: 'not-allowed',
        },
    },
    presetActive: {
        backgroundColor: palette.accent,
        color: palette.primaryText,
        border: `1px solid ${palette.accent}`,
        boxShadow: palette.softShadowSm,
        '&:hover:not(:disabled)': {
            backgroundColor: palette.accentHover,
            color: palette.primaryText,
            border: `1px solid ${palette.accentHover}`,
        },
    },

    commitRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '14px',
        flexWrap: 'wrap',
    },
    customField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    customLabel: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightBold,
        color: palette.dimText,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
    },
    spin: {
        height: '38px',
        width: '110px',
        backgroundColor: palette.panelBg,
    },
    addButton: {
        marginLeft: 'auto',
        height: '38px',
    },
    addIcon: {
        width: '16px',
        height: '16px',
    },
});
